// Customer portal dashboard / Overview (Session AG Item 5; Session AFH Jobber-hub redesign). A
// plain-English compliance summary: a tile row (total lifts, lifts with a current LOLER examination,
// lifts overdue for service), any outstanding invoices with a Pay now button, and the customer's
// sites as a flat row-list (each with its worst status dot, linking to the site detail page). The
// per-lift detail lives on the site page; the document library now has its own section
// (/portal/documents). NO pricing/contract value, engineer names or internal notes. A lapsed
// session (401) sends the customer back to sign in.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PortalOverview } from "../../lib/api";
import { PortalShell } from "./PortalShell";
import { PortalSiteList } from "./PortalSiteList";
import { OpenWorkSection } from "./PortalOpenWork";
import { FullScreenLoader } from "../../components/Loader";
import { ContactValue } from "../../components/ContactValue";

export function PortalDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<PortalOverview | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    api.portal
      .overview()
      .then((d) => {
        if (live) setData(d);
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) navigate("/portal/login", { replace: true });
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [navigate]);

  async function logout() {
    try {
      await api.portal.logout();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  }

  if (error) {
    return (
      <PortalShell>
        <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">Something went wrong</h1>
          <p className="text-ele-body">
            Please try again in a moment, or call the office on{" "}
            <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
          </p>
        </div>
      </PortalShell>
    );
  }
  if (!data) return <FullScreenLoader />;

  return (
    <PortalShell wide userName={data.customerName} onSignOut={logout}>
      <header className="mb-6">
        {/* Session AGY: a time-of-day greeting eyebrow. Session AGZ (Director decision): the
            greeting addresses the customer by the FIRST WORD of customer.name -- a personal
            first name is still not available client-side (the /portal/me shape returns only
            email + customer.name), so the first word of the account name is the agreed
            approximation, and the full name stays the headline below. */}
        {/* Muted ink inline: Tailwind v3 emits nothing for alpha on a var() colour. */}
        <p className="font-menu text-xs uppercase tracking-wider" style={{ color: "rgba(202, 202, 202, 0.6)" }}>
          {greetingLine(new Date().getHours(), data.customerName)}
        </p>
        <h1 className="font-heading text-3xl tracking-wide text-ele-white">{data.customerName}</h1>
        <p className="mt-1 text-ele-body">Welcome. Here is the compliance status for your portfolio.</p>
      </header>

      {/* Session ACG value colour logic: total lifts = blue (reference data); LOLER count =
          green when every lift is current, amber attention otherwise; overdue = red if any,
          else green. Session AGY: two-up tile grid on phones (the first tile spans the row). */}
      <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Tile label="Lifts" value={data.summary.totalLifts} className="max-sm:col-span-2" />
        <Tile
          label="Lifts with a current LOLER"
          value={data.summary.liftsWithCurrentLoler}
          tone={data.summary.totalLifts > 0 && data.summary.liftsWithCurrentLoler === data.summary.totalLifts ? "green" : "amber"}
        />
        <Tile label="Lifts overdue for service" value={data.summary.liftsOverdueForService} tone={data.summary.liftsOverdueForService > 0 ? "red" : "green"} />
      </div>

      {/* Session AUD (email system Phase 5, Item 3): open work and the way to report a fault. */}
      <OpenWorkSection />

      {data.invoicesUnconfirmed === true && (
        // Clearance 2 (MONEY-1): an invoice we could not confirm with our accounts system is left out; say so rather than imply nothing is owed.
        <p role="status" className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">
          We could not check your invoices just now, so the list below may be incomplete. Please try again shortly, or see{" "}
          <Link to="/portal/invoices" className="text-ele-green hover:underline">
            all invoices
          </Link>
          . You can also call the office on 0203 576 4760.
        </p>
      )}

      {data.outstandingInvoices.length > 0 && (
        <Section
          title="Outstanding invoices"
          // Phase 8 (Session ATJ, M-9a): the full Invoices view (outstanding + paid, live status),
          // reached from here rather than an eighth top-level nav tab.
          action={
            <Link to="/portal/invoices" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
              See all invoices
            </Link>
          }
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {data.outstandingInvoices.map((inv, i) => (
              <div key={i} className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4">
                <div className="flex items-baseline justify-between">
                  <span className="font-heading text-lg text-ele-white">{inv.amountDisplay}</span>
                  <span className="text-xs text-amber-400">{inv.statusLabel}</span>
                </div>
                {inv.dueDateDisplay && <p className="text-sm text-ele-body">Due {inv.dueDateDisplay}</p>}
                {inv.payUrl && (
                  <a
                    href={inv.payUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-block rounded-ele bg-ele-green px-4 py-2 font-heading text-sm tracking-wide text-ele-bg"
                  >
                    Pay now
                  </a>
                )}
                {!inv.payUrl && (
                  <Link to="/portal/invoices" className="mt-3 inline-block font-menu text-xs tracking-wide text-ele-green hover:underline">
                    Pay or view on the Invoices page
                  </Link>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Your sites">
        <PortalSiteList sites={data.sites} />
      </Section>
    </PortalShell>
  );
}

// Session AGY: the time-of-day greeting (pure; hour in local time).
export function greetingForHour(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

// Session AGZ (Director decision): greet by the first space-separated word of the account
// name -- "Good morning, Rosemede" beats the full company name in a greeting, and the
// /portal/me shape carries no personal name to do better with. Pure and exported for tests.
// A blank or whitespace-only name degrades to the plain time-of-day greeting.
export function greetingLine(hour: number, customerName: string): string {
  const first = (customerName ?? "").trim().split(/\s+/)[0] ?? "";
  return first ? `${greetingForHour(hour)}, ${first}` : greetingForHour(hour);
}

function Tile({
  label,
  value,
  tone = "neutral",
  className = "",
}: {
  label: string;
  value: number;
  tone?: "neutral" | "red" | "green" | "amber";
  className?: string;
}) {
  // Session ACG colour logic: neutral = the reference-data blue (#2CB9F3); green = all in
  // order; amber = attention needed; red = something overdue.
  const valueTone =
    tone === "red" ? "text-ele-danger" : tone === "green" ? "text-ele-green" : tone === "amber" ? "text-amber-400" : "text-ele-loler";
  return (
    <div className={`rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 ${className}`}>
      <p className="font-menu text-xs uppercase tracking-wider text-ele-body">{label}</p>
      <p className={`mt-1 font-heading text-3xl ${valueTone}`}>{value}</p>
    </div>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="mb-8">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="font-heading text-xl text-ele-purple">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
